/**
 * Sponsor profile effects - side effect handlers.
 * 
 * Elm-ish architecture: Side effects are isolated here.
 * The effect runner (useSponsorProfileProgram.ts) calls these functions.
 * 
 * Security posture:
 * - All API calls use identity-minimized endpoints
 * - No credentials logged
 * - Safe error messages only
 * - Server-side authorization enforced (sponsor-only)
 * - Fingerprint collection is non-blocking (fail-open)
 * 
 * Profile activation per REQ-125, REQ-126, REQ-127:
 * - Sends gender/goal/age with profile updates
 * - Reads profile_status and missing_activation_fields from API
 */
import { getSponsorProfile, createOrUpdateSponsorProfile, ProfileError } from '@/api/profiles';
import { profileToForm, formToUpdatePayload } from './model';
import type { SponsorProfileEffect } from './update';
import type { ProfileFormData } from './model';
import { collectFingerprint } from '@/utils/fingerprint';

/**
 * Effect handler context - injected by the effect runner.
 */
export interface EffectContext {
  dispatch: (msg: import('./messages').SponsorProfileMsg) => void;
}

/**
 * Run a single effect.
 * Returns a cleanup function if needed.
 */
export async function runEffect(
  effect: SponsorProfileEffect,
  context: EffectContext
): Promise<(() => void) | void> {
  switch (effect.type) {
    case 'LoadProfile':
      return runLoadProfile(context);
    
    case 'SaveProfile':
      return runSaveProfile(context, effect.form);
    
    default:
      const _exhaustive: never = effect;
      return;
  }
}

/**
 * Load the authenticated sponsor's profile.
 */
async function runLoadProfile(context: EffectContext): Promise<void> {
  try {
    const profile = await getSponsorProfile();
    
    // Extract activation state from API response per REQ-125
    const profileStatus = profile?.profile_status || 'inactive';
    const missingActivationFields = profile?.missing_activation_fields || [];
    
    context.dispatch({
      type: 'LoadSucceeded',
      profile,
      profileStatus,
      missingActivationFields,
    });
  } catch (err) {
    if (err instanceof ProfileError) {
      // 403 means sponsor account doesn't have access (shouldn't happen, but handle gracefully)
      if (err.statusCode === 403) {
        context.dispatch({
          type: 'LoadFailed',
          error: 'You do not have permission to access sponsor profiles',
        });
        return;
      }
      // 404 means no profile yet - this is OK, show empty state
      if (err.statusCode === 404) {
        context.dispatch({
          type: 'LoadSucceeded',
          profile: null,
          profileStatus: 'inactive',
          missingActivationFields: ['gender', 'goal', 'age'],
        });
        return;
      }
      // Other errors (401, 5xx) are unsafe - surface error
      context.dispatch({
        type: 'LoadFailed',
        error: 'Failed to load sponsor profile',
      });
    } else {
      context.dispatch({
        type: 'LoadFailed',
        error: 'Network error loading sponsor profile',
      });
    }
  }
}

/**
 * Save the sponsor's profile with fingerprint for fraud-resistant audit context.
 * Fingerprint collection is non-blocking - profile saves even if collection fails.
 * 
 * Per REQ-125, REQ-126, REQ-127:
 * - Sends gender, goal, and age with the profile update
 * - formToUpdatePayload respects gender-first rule (goal only sent if gender is set)
 */
async function runSaveProfile(context: EffectContext, form: ProfileFormData): Promise<void> {
  try {
    // Collect fingerprint for fraud-resistant audit context (fail-open)
    let fingerprint;
    try {
      fingerprint = await collectFingerprint();
    } catch {
      // Fingerprint collection failed - continue without it
      fingerprint = undefined;
    }
    
    const payload = formToUpdatePayload(form);
    
    const profile = await createOrUpdateSponsorProfile({
      ...payload,
      fingerprint,
    });
    
    // Extract activation state from API response per REQ-125
    const profileStatus = profile.profile_status || 'inactive';
    const missingActivationFields = profile.missing_activation_fields || [];
    
    context.dispatch({
      type: 'SaveSucceeded',
      profile,
      profileStatus,
      missingActivationFields,
    });
  } catch (err) {
    if (err instanceof ProfileError) {
      // 403 means friend account trying to create sponsor profile (shouldn't happen)
      if (err.statusCode === 403) {
        context.dispatch({
          type: 'SaveFailed',
          error: 'You do not have permission to create sponsor profiles',
        });
        return;
      }
      // 401, 5xx are unsafe
      context.dispatch({
        type: 'SaveFailed',
        error: 'Failed to save sponsor profile',
      });
    } else {
      context.dispatch({
        type: 'SaveFailed',
        error: 'Network error saving sponsor profile',
      });
    }
  }
}
